/**
 * BureauTable — sheet 31's bureau rows (mission 7 WP10 T053; FR-011, FR-028; TX.1, TX.2, TX.4, TX.9, GEN.3,
 * GEN.6, UC-01; sheet 31 callouts 4 to 9 and 12; squad P15; contracts/ui-api.md "Sheets 31 and 33").
 *
 * A `<table>` with the `transmitCol.*` headers in `<th scope="col">` and one row per bureau in `BUREAUS`
 * order, whatever order the read gives. Per row:
 * - the checkbox, named `transmitSelectBureau`, enabled only on a verified row (TX.2); the page owns the
 *   selection;
 * - `BureauStatusBadge`, with the last successful test as the verified date;
 * - the masked subscriber code linking to Settings › Header parameters, with no reveal
 *   (DM-01M3BGTA0D7JMNSAMXC12E16MT); a code not entered says `notEntered` with the GEN.6 sentence beside it
 *   (P15);
 * - `remote_path` and the per-run read's remote filename, in mono (31/7);
 * - `lastTransmission` from the newest attempt of this run to that bureau, in `<time dateTime>`, and
 *   `lastTransmissionUncertain` when that attempt timed out (index open question 57; OR-5);
 * - every other status's reason and link: entered never tested and auth failed link to Settings › Bureau
 *   credentials, not entered offers download and deliver yourself (UC-01), disabled states the kill switch
 *   with no reason (31/12's deviation; T.3, X.1), and a host refusal names its remedy.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { BureauStatusBadge } from '@/components/trellis/BureauStatusBadge'
import { MaskedValue } from '@/components/trellis/MaskedValue'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { BUREAUS, bureauName, type Bureau, type BureauEntry } from '@/lib/api/bureaus'
import type { TransmissionEntry } from '@/lib/api/transmissions'
import { Mono, date, isoDateTime, time } from '@/lib/format'
import { PROVISIONAL_COPY, copyText, type TextKey } from '@/lib/provisional-copy'

export interface BureauTableProps {
  runId: string
  bureaus: readonly BureauEntry[]
  /** The per-run read's entries, newest first; the newest per bureau is its last transmission. */
  entries: readonly TransmissionEntry[]
  /** The per-run read's `remote_filename`; absent when the run is not eligible. */
  remoteFilename: string | undefined
  selected: ReadonlySet<Bureau>
  onToggle(bureau: Bureau, checked: boolean): void
  /** The bureau a `host-not-allowed` refusal named (SEC.1). */
  hostRefused: Bureau | null
}

const LINK = 'text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]'
const SETTINGS_BUREAUS = '/app/settings/bureaus'

function newest(entries: readonly TransmissionEntry[], bureau: Bureau): TransmissionEntry | undefined {
  let found: TransmissionEntry | undefined
  for (const e of entries) {
    if (e.bureau === bureau && (found === undefined || Date.parse(e.attempted_at) > Date.parse(found.attempted_at))) found = e
  }
  return found
}

function Reason({ children }: { children: ReactNode }) {
  return (
    <p data-row-reason="" className="mt-1 text-body-sm text-navy-700">
      {children}
    </p>
  )
}

function ReasonLink({ to, text }: { to: string; text: TextKey }) {
  return (
    <Reason>
      <Link to={to} className={LINK}>
        {copyText(text)}
      </Link>
    </Reason>
  )
}

function RowReason({ entry, runId, hostRefused }: { entry: BureauEntry; runId: string; hostRefused: boolean }) {
  if (hostRefused) return <ReasonLink to={SETTINGS_BUREAUS} text="hostNotAllowedRemedy" />
  switch (entry.status) {
    case 'verified':
      return null
    case 'entered_untested':
      return <ReasonLink to={SETTINGS_BUREAUS} text="rowReason.enteredUntested" />
    case 'auth_failed':
      return <ReasonLink to={SETTINGS_BUREAUS} text="rowReason.authFailed" />
    case 'disabled_by_trellis':
      return <Reason>{copyText('killSwitchExplanation')}</Reason>
    case 'not_entered':
      return (
        <Reason>
          {copyText('rowReason.notEntered')}{' '}
          <Link to={`/app/runs/${encodeURIComponent(runId)}/download`} className={LINK}>
            {copyText('downloadAndDeliverLink')}
          </Link>
        </Reason>
      )
  }
}

function SubscriberCode({ entry }: { entry: BureauEntry }) {
  const code = entry.subscriber_code
  if (code.set && code.masked) {
    return (
      <Link to="/app/settings/header" className={LINK}>
        <MaskedValue value={code.masked} />
      </Link>
    )
  }
  return (
    <>
      <Link to="/app/settings/header" className={LINK}>
        {copyText('notEntered')}
      </Link>
      <p className="mt-1 text-body-sm text-navy-700">{copyText('notEnteredGen6')}</p>
    </>
  )
}

function LastTransmission({ entry }: { entry: TransmissionEntry | undefined }) {
  if (!entry) return null
  const at = `${date(entry.attempted_at)} ${time(entry.attempted_at)}`
  const text = PROVISIONAL_COPY.lastTransmission.render({ time: at, result: entry.result })
  const i = text.indexOf(at)
  return (
    <>
      <p data-last-transmission="" className="text-body-sm text-navy-950">
        {text.slice(0, i)}
        <time dateTime={isoDateTime(entry.attempted_at)}>
          <Mono>{at}</Mono>
        </time>
        {text.slice(i + at.length)}
      </p>
      {entry.result === 'timeout' ? (
        <p className="mt-1 text-body-sm text-navy-700">{copyText('lastTransmissionUncertain')}</p>
      ) : null}
    </>
  )
}

export function BureauTable({ runId, bureaus, entries, remoteFilename, selected, onToggle, hostRefused }: BureauTableProps) {
  const rows = BUREAUS.flatMap((b) => bureaus.filter((e) => e.bureau === b))
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">{copyText('transmitCol.bureau')}</TableHead>
          <TableHead scope="col">{copyText('transmitCol.status')}</TableHead>
          <TableHead scope="col">{copyText('transmitCol.subscriberCode')}</TableHead>
          <TableHead scope="col">{copyText('transmitCol.remotePath')}</TableHead>
          <TableHead scope="col">{copyText('transmitCol.remoteFilename')}</TableHead>
          <TableHead scope="col">{copyText('transmitCol.lastTransmission')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((entry) => {
          const verified = entry.status === 'verified'
          return (
            <TableRow key={entry.bureau} data-bureau={entry.bureau} className="h-auto align-top">
              <TableCell className="py-3">
                <span className="inline-flex items-center gap-2">
                  <Checkbox
                    aria-label={PROVISIONAL_COPY.transmitSelectBureau.render({ bureau: entry.bureau })}
                    disabled={!verified}
                    checked={verified && selected.has(entry.bureau)}
                    onCheckedChange={(checked) => onToggle(entry.bureau, checked === true)}
                  />
                  {bureauName(entry.bureau)}
                </span>
              </TableCell>
              <TableCell className="py-3">
                <BureauStatusBadge status={entry.status} verifiedAt={entry.credential?.last_tested_at ?? null} />
                <RowReason entry={entry} runId={runId} hostRefused={hostRefused === entry.bureau} />
              </TableCell>
              <TableCell className="py-3">
                <SubscriberCode entry={entry} />
              </TableCell>
              <TableCell className="py-3">
                {entry.credential ? <Mono className="text-body-sm">{entry.credential.remote_path}</Mono> : null}
              </TableCell>
              <TableCell className="py-3">
                {remoteFilename ? <Mono className="text-body-sm">{remoteFilename}</Mono> : null}
              </TableCell>
              <TableCell className="py-3">
                <LastTransmission entry={newest(entries, entry.bureau)} />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
